import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { MessageCircle, Calendar as CalendarIcon, Users, Phone } from 'lucide-react';
import 'react-calendar/dist/Calendar.css';
import './AvailabilityCalendar.css';

const AvailabilityCalendar = () => {
  const [bookedDates, setBookedDates] = useState([]);
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [selectedCheckOut, setSelectedCheckOut] = useState(null);
  const [guestCount, setGuestCount] = useState(2);
  const [loading, setLoading] = useState(true);
  const [selectingCheckOut, setSelectingCheckOut] = useState(false);

  const AIRBNB_ICAL_URL = 'https://www.airbnb.com/calendar/ical/914998361984649566.ics?t=a8ef62e00ae34d31919062a814d5220e&locale=en-GB';

  useEffect(() => {
    fetchAirbnbCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAirbnbCalendar = async () => {
    try {
      // Fetch through a CORS proxy
      const response = await fetch(`https://corsproxy.io/?${encodeURIComponent(AIRBNB_ICAL_URL)}`);
      const icalData = await response.text();
      
      const blocked = parseICalData(icalData);
      setBookedDates(blocked);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      setLoading(false);
    }
  };

  const parseICalData = (icalText) => {
    const blockedDates = [];
    const lines = icalText.split('\n');
    let currentEvent = {};

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === 'BEGIN:VEVENT') {
        currentEvent = {};
      } else if (line.startsWith('DTSTART')) {
        const dateStr = line.split(':')[1];
        currentEvent.start = parseDateString(dateStr);
      } else if (line.startsWith('DTEND')) {
        const dateStr = line.split(':')[1];
        currentEvent.end = parseDateString(dateStr);
      } else if (line === 'END:VEVENT' && currentEvent.start && currentEvent.end) {
        // Add all dates in the range
        const dates = getDateRange(currentEvent.start, currentEvent.end);
        blockedDates.push(...dates);
      }
    }

    return blockedDates;
  };

  const parseDateString = (dateStr) => {
    // Format: YYYYMMDD
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return new Date(year, month - 1, day);
  };

  const getDateRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    
    while (currentDate < end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const isDateBooked = (date) => {
    return bookedDates.some(bookedDate => 
      bookedDate.toDateString() === date.toDateString()
    );
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      if (isDateInPast(date)) {
        return 'past-date';
      }
      if (isDateBooked(date)) {
        return 'booked-date';
      }
      if (selectedCheckIn && date.toDateString() === selectedCheckIn.toDateString()) {
        return 'selected-checkin';
      }
      if (selectedCheckOut && date.toDateString() === selectedCheckOut.toDateString()) {
        return 'selected-checkout';
      }
      if (selectedCheckIn && selectedCheckOut && date > selectedCheckIn && date < selectedCheckOut) {
        return 'selected-range';
      }
    }
    return null;
  };

  const tileDisabled = ({ date, view }) => {
    if (view === 'month') {
      // Disable past dates and booked dates
      if (isDateInPast(date) || isDateBooked(date)) {
        return true;
      }
      
      // If selecting checkout, disable dates before checkin
      if (selectingCheckOut && selectedCheckIn && date <= selectedCheckIn) {
        return true;
      }
    }
    return false;
  };

  const handleDateClick = (date) => {
    if (isDateInPast(date) || isDateBooked(date)) {
      return;
    }

    if (!selectingCheckOut) {
      // First click - select check-in
      setSelectedCheckIn(date);
      setSelectedCheckOut(null);
      setSelectingCheckOut(true);
    } else {
      // Second click - select check-out
      if (date > selectedCheckIn) {
        // Check if any dates in between are blocked
        const datesInBetween = getDateRange(selectedCheckIn, date);
        const hasBlockedDate = datesInBetween.some(d => isDateBooked(d));
        
        if (hasBlockedDate) {
          alert('Selected date range contains booked dates. Please select different dates.');
          return;
        }
        
        setSelectedCheckOut(date);
        setSelectingCheckOut(false);
      }
    }
  };

  const resetSelection = () => {
    setSelectedCheckIn(null);
    setSelectedCheckOut(null);
    setSelectingCheckOut(false);
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const calculateNights = () => {
    if (!selectedCheckIn || !selectedCheckOut) return 0;
    const diffTime = Math.abs(selectedCheckOut - selectedCheckIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const generateWhatsAppMessage = () => {
    if (!selectedCheckIn || !selectedCheckOut) {
      return "Hi, I'm interested in booking Leoland by VengaFarms. Can you please share availability and pricing?";
    }

    const nights = calculateNights();
    const message = `Hi! I'd like to book Leoland by VengaFarms:

📅 Check-in: ${formatDateForDisplay(selectedCheckIn)}
📅 Check-out: ${formatDateForDisplay(selectedCheckOut)}
🌙 Nights: ${nights}
👥 Guests: ${guestCount}

Please confirm availability and send payment details. Thank you!`;

    return encodeURIComponent(message);
  };

  const whatsappUrl = `https://wa.me/918008002388?text=${generateWhatsAppMessage()}`;
  const phoneUrl = `tel:+918008002388`;

  return (
    <div className="availability-calendar-container">
      <div className="calendar-header">
        <span className="label">09 &mdash; Real-Time Availability</span>
        <h2 className="display-2">Check Availability &amp; Book</h2>
        <p className="lede" style={{ marginTop: '0.75rem' }}>
          Select your check-in and check-out dates, then book via WhatsApp
        </p>
      </div>

      <div className="calendar-content">
        <div className="calendar-wrapper">
          {loading ? (
            <div className="calendar-loading">
              <div className="spinner"></div>
              <p>Loading availability...</p>
            </div>
          ) : (
            <>
              <Calendar
                onChange={handleDateClick}
                value={selectedCheckIn}
                tileClassName={tileClassName}
                tileDisabled={tileDisabled}
                minDate={new Date()}
                showDoubleView={window.innerWidth > 768}
                className="custom-calendar"
              />
              
              <div className="calendar-legend">
                <div className="legend-item">
                  <div className="legend-color available"></div>
                  <span>Available</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color booked"></div>
                  <span>Booked</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color selected"></div>
                  <span>Selected</span>
                </div>
              </div>

              <div className="calendar-instructions">
                {!selectingCheckOut ? (
                  <p>👆 Click a date to select <strong>Check-in</strong></p>
                ) : (
                  <p>👆 Click a date to select <strong>Check-out</strong></p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="booking-summary">
          <h3 className="booking-summary-title">Booking Details</h3>

          <div className="booking-detail-item">
            <CalendarIcon size={20} color="#c1531f" />
            <div>
              <div className="detail-label">Check-in</div>
              <div className="detail-value">
                {selectedCheckIn ? formatDateForDisplay(selectedCheckIn) : 'Select date'}
              </div>
            </div>
          </div>

          <div className="booking-detail-item">
            <CalendarIcon size={20} color="#c1531f" />
            <div>
              <div className="detail-label">Check-out</div>
              <div className="detail-value">
                {selectedCheckOut ? formatDateForDisplay(selectedCheckOut) : 'Select date'}
              </div>
            </div>
          </div>

          {selectedCheckIn && selectedCheckOut && (
            <div className="booking-detail-item">
              <div className="nights-badge">
                {calculateNights()} Night{calculateNights() !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          <div className="booking-detail-item">
            <Users size={20} color="#c1531f" />
            <div style={{ flex: 1 }}>
              <div className="detail-label">Number of Guests</div>
              <select
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value)}
                className="guest-select"
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} Guest{i + 1 !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(selectedCheckIn || selectedCheckOut) && (
            <button onClick={resetSelection} className="reset-btn">
              Reset Dates
            </button>
          )}

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-whatsapp calendar-cta"
          >
            <MessageCircle size={18} />
            Book via WhatsApp
          </a>

          <a href={phoneUrl} className="btn btn-solid-dark calendar-cta">
            <Phone size={18} />
            Call: +91 80080 02388
          </a>

          <div className="quick-response-note">
            <strong>Quick Response:</strong> Our team will confirm availability and send payment details within 30 minutes!
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
